import { shareRenderData } from "./renderSharing";
import { indexCharacterMetadata } from "./characterMetadata";
import { SubToPageContent_page } from "./SubToPageContent";
import { SubToPages_pages } from "./SubToPages";

import { SubToPagesExtended_extendedPages } from "./__generated__/SubToPagesExtended";
import { SubscribeToExtendedPage_extendedPage } from "./__generated__/SubscribeToExtendedPage";

const NO_KINDS: readonly string[] = [];

export enum LetterKind {
  CITATION = "CITATION",
  DEFAULT = "DEFAULT",
  EXPOSANT = "EXPOSANT",
  GRAS = "GRAS",
  INDICE = "INDICE",
  ITALIQUE = "ITALIQUE",
  MAJUSCULE = "MAJUSCULE",
  MINUSCULE = "MINUSCULE",
  MUTE = "MUTE",
}

// The legacy query model describes tag as an object; extended metadata supplies its name.
type ConvertedPage = Omit<SubToPageContent_page, "paragraphs" | "definitions"> & {
  paragraphs: (Omit<SubToPageContent_page["paragraphs"][number], "tag"> & { tag: string | undefined })[];
  definitions: (Omit<SubToPageContent_page["definitions"][number], "definition"> & { definition: string | null })[];
};

export const convertPageContent = (
  projectId: string,
  pageContent: SubscribeToExtendedPage_extendedPage,
): ConvertedPage => {
  const output: ConvertedPage = {
    __typename: "Page",
    numberOfRheses: pageContent?.rhese.length,
    paragraphs: [],
    rheses: [],
    definitions: [],
  };

  pageContent?.definition.forEach((definition) => {
    output.definitions.push({
      __typename: "Definition",
      id: definition.data.id,
      kind: definition.data.kind,
      definition: definition.data.description,
      imageName: definition.data.imageName,
      audioFile: definition.data.audioFile,
      audioReviewComment: definition.data.audioReviewComment,
      audioReviewStatus: definition.data.audioReviewStatus,
      start: definition.anchors[0].utf16Start,
      size: definition.anchors[0].utf16Size,
    });
  });
  pageContent?.paragraph.forEach((paragraph) => {
    output.paragraphs.push({
      __typename: "Paragraph",
      id: paragraph.anchors[0].externalId,
      kind: paragraph.data.kind,
      tag: paragraph.data.tag?.name,
      dialogueSpeakerId: paragraph.data.speaker?.id,
      projectId,
    });
  });

  const attributes = pageContent && indexCharacterMetadata(pageContent);
  let rhesePosition = 0;
  pageContent?.rhese.forEach((rhese) => {
    const newRhese = {
      __typename: "SqlxRhese",
      id: rhese.anchors[0].externalId,
      projectId,
      ignoreTextForAudioSync: rhese.data.ignoreTextForAudioSync,
      lineBreakAfter: rhese.data.lineBreakAfter,
      start: rhese.anchors[0].utf16Start,
      size: rhese.anchors[0].utf16Size,
      paragraphId: null,
      position: rhesePosition++,
      audioRecordingEnd: null,
      audioRecordingStart: null,
      audioRecordingId: null,
      letters: [],
    };

    const pageStartOffset = pageContent?.page.anchors[0].utf16Start;
    const start = rhese.anchors[0].utf16Start;
    const offsettedStart = start - pageStartOffset;
    const size = rhese.anchors[0].utf16Size;

    const text = pageContent?.textContent.substring(offsettedStart, offsettedStart + size);

    for (let paragraph of pageContent?.paragraph) {
      let paragraphStart = paragraph.anchors[0].utf16Start;
      let paragraphSize = paragraph.anchors[0].utf16Size;
      if (paragraphStart <= start && paragraphStart + paragraphSize >= start + size) {
        newRhese.paragraphId = paragraph.anchors[0].externalId;
        break;
      }
    }

    for (let i = 0; i < text.length; i++) {
      const position = start + i - attributes.start;
      const letterKind = ["DEFAULT", ...(attributes.kinds[position] || NO_KINDS)];
      const letterIsMuted = !!attributes.muted[position];
      const phonemeKind = attributes.phoneme[position] ?? null;
      const syllableId = attributes.syllable[position] ?? null;
      const customColorationKey = attributes.coloration[position] ?? null;
      const cssClasses = attributes.css[position] ?? null;

      let letter = {
        __typename: "SqlxLetter",
        id: null,
        isMute: letterIsMuted,
        char: text.charAt(i),
        phoneme: phonemeKind,
        absoluteTextPosition: start + i, // Position from the beginning of the text
        position: i, // Position from the beginning of the rhese
        syllableId,
        customColoration: customColorationKey,
        kind: letterKind,
        cssClasses,
      };

      // @ts-ignore
      newRhese.letters.push(letter);
    }
    // @ts-ignore
    output.rheses.push(newRhese);
  });

  // @ts-ignore
  output.rheses.sort((a, b) => a.start - b.start);

  return output;
};

/** Bounded, instance-local cache. Reuse equal output without changing conversion semantics. */
export function createPageConverter() {
  let previous: ConvertedPage | undefined;
  return (projectId: string, pageContent: SubscribeToExtendedPage_extendedPage) => {
    previous = shareRenderData(previous, convertPageContent(projectId, pageContent));
    return previous;
  };
}

export const convertExtendedPagesToPages = (
  data: SubToPagesExtended_extendedPages | never,
): SubToPages_pages[] => {
  const output: SubToPages_pages[] = [];

  const pagesById = new Map<string, typeof data.extendedPages[number]>();
  for (const page of data.extendedPages) if (!pagesById.has(page.id)) pagesById.set(page.id, page);
  data.computedMetadata?.forEach((computedMetadata) => {
    const extendedPage = pagesById.get(computedMetadata.externalId);
    if (!extendedPage) {
      return;
    }
    output.push({
      __typename: "Page",
      id: extendedPage.id,
      mutePagesAfter: extendedPage.mutePagesAfter,
      pageNumber: extendedPage.pageNumber.pageNumber,
      lastPageNumber: extendedPage.pageNumber.lastPageNumber,
      partName: extendedPage.partname || null,

      // New infos
      start: computedMetadata.utf16Start,
      size: computedMetadata.utf16Size,
      stringVersion: computedMetadata.stringVersion,

      // Old infos that does not meet current requirements
      numberOfRheses: 0, // placeholder
      wordsCount: null, // placeholder
      issuesCount: 0, // placeholder
      renarratedCount: 0, // placeholder
      fullyRecorded: false, // placeholder
    });
  });

  return output.sort((a, b) => a.pageNumber - b.pageNumber);
};
